'use client';
import { useParams, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Copyright } from '~components/Copyright';
import { ToC } from '~components/ToC';
import { routes } from '~constants/routes';
import { useChatReviewContext } from '~context/chatReview';
import { useDialogs } from '~entities/dialogs';
import { FeedbackTrigger, PaymentReason } from '~features/annals';
import { usePaymentReasonType } from '~features/annals/hooks/checkout';
import { BotPaywallModal } from '~features/botPaywall';
import { useChatAutoModeSwitch } from '~features/nsfw';
import { useBotColors } from '~hooks/useBotColors';
import { useRouter } from '~i18n/navigation';
import { Contacts } from '~widgets/Contacts';

import { Container, SiteFooter, Wrapper } from './styles';

export const ChatAside = () => {
	const { recipientId } = useParams<{ recipientId: string }>();
	const searchParams = useSearchParams();
	const { replace } = useRouter();
	const [shopBotClientId, setShopBotClientId] = useState<number>();
	const reasonType = usePaymentReasonType();
	const { bots } = useDialogs(recipientId);
	const backTo = useRef<string | null>(null);

	useChatAutoModeSwitch();
	useBotColors();
	const { askForReview } = useChatReviewContext();

	const handleCloseShop = useCallback(
		(isSuccess: boolean) => {
			setShopBotClientId(undefined);

			if (!isSuccess && backTo.current) {
				replace(backTo.current);
			}
		},
		[replace]
	);

	const handleAboveLevelBotClick = useCallback((botClientId: number) => {
		setShopBotClientId(botClientId);
	}, []);

	const handleAskReview = useCallback(() => {
		askForReview({ force: true, source: FeedbackTrigger.Explicit });
	}, [askForReview]);

	useEffect(() => {
		const id = Number(recipientId);
		const current = bots.find((b) => b.clientId === id);

		if (current?.disabledByLevel) {
			if (searchParams.get('backTo') === 'profile') {
				backTo.current = routes.profile.slug(recipientId);
			}

			replace(routes.chats.home);
			setShopBotClientId(id);
		}
	}, [bots, recipientId, replace, searchParams]);

	return (
		<>
			<Wrapper id="chat-aside">
				<Container>
					<Contacts onAboveLevelBotClick={handleAboveLevelBotClick} />
				</Container>

				<SiteFooter>
					<ToC onReviewClick={handleAskReview} />
					<Copyright />
				</SiteFooter>
			</Wrapper>

			{/* TODO: Open by openBotPaywall from context */}
			<BotPaywallModal
				botClientId={shopBotClientId}
				reason={PaymentReason.LockedBot}
				reasonType={reasonType}
				redirect={true}
				onClose={handleCloseShop}
			/>
		</>
	);
};
